import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { ConfigProvider, message } from 'antd';
import React from 'react';
import { fetchCurrentUser, createGuestSession } from '@/services/auth';
import { fetchMyModules, fetchMyReports } from '@/services/users';
import { fetchProductInfo } from '@/services/productInfo';

const LOGIN_PATH = '/user/login';
const CHANGE_PASSWORD_PATH = '/user/change-password';
const GUEST_RETRY_KEY = 'biportal_guest_retry';

/**
 * Soft validation toasts (message.*) — viewport-centered like OC Portal.
 * Blocking 403/5xx still use PortalErrorPrompt (centered Modal).
 */
message.config({
  duration: 3.5,
  maxCount: 4,
  top: '18vh',
});

/** Static Modal.error / confirm inherit centered placement (antd 6 holder). */
ConfigProvider.config({
  holderRender: (children) =>
    React.createElement(ConfigProvider, { modal: { centered: true } }, children),
});

export interface InitialState {
  currentUser?: API.CurrentUser;
  productInfo?: API.PublicProductInfo;
  userReports?: string[];
  userModules?: string[];
  permissionsLoaded?: boolean;
  loading?: boolean;
}

function isPublicPath(pathname: string): boolean {
  return pathname === LOGIN_PATH || pathname.startsWith('/user/reset-password');
}

async function loadAuthenticatedState(
  productInfo?: API.PublicProductInfo,
): Promise<InitialState> {
  const { location } = history;
  const currentUser = await fetchCurrentUser();
  if (currentUser.must_change_password && location.pathname !== CHANGE_PASSWORD_PATH) {
    history.replace(CHANGE_PASSWORD_PATH);
  }
  let userReports: string[] = [];
  let userModules: string[] = [];
  try {
    [userReports, userModules] = await Promise.all([fetchMyReports(), fetchMyModules()]);
  } catch {
    userReports = [];
    userModules = [];
  }
  return {
    currentUser,
    productInfo,
    userReports,
    userModules,
    permissionsLoaded: true,
  };
}

export async function getInitialState(): Promise<InitialState> {
  const { location } = history;

  let productInfo: API.PublicProductInfo | undefined;
  try {
    productInfo = await fetchProductInfo();
  } catch {
    productInfo = undefined;
  }

  if (isPublicPath(location.pathname)) {
    return { productInfo };
  }

  try {
    return await loadAuthenticatedState(productInfo);
  } catch {
    if (productInfo?.open_access_enabled) {
      try {
        await createGuestSession();
        sessionStorage.removeItem(GUEST_RETRY_KEY);
        return await loadAuthenticatedState(productInfo);
      } catch {
        history.replace(LOGIN_PATH);
        return { productInfo };
      }
    }
    history.replace(LOGIN_PATH);
    return { productInfo };
  }
}

export const request: RequestConfig = {
  timeout: 15000,
  withCredentials: true,
  errorConfig:
    {
    errorHandler(error: any) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const detail = data?.detail;
      const code =
        typeof detail === 'object' && detail !== null
          ? detail.code
          : data?.code;

      if (status === 401) {
        const { pathname } = history.location;
        if (isPublicPath(pathname)) {
          return;
        }
        if (sessionStorage.getItem(GUEST_RETRY_KEY) === '1') {
          sessionStorage.removeItem(GUEST_RETRY_KEY);
          history.replace(LOGIN_PATH);
          return;
        }
        sessionStorage.setItem(GUEST_RETRY_KEY, '1');
        void createGuestSession()
          .then(() => {
            sessionStorage.removeItem(GUEST_RETRY_KEY);
            window.location.reload();
          })
          .catch(() => {
            sessionStorage.removeItem(GUEST_RETRY_KEY);
            history.replace(LOGIN_PATH);
          });
        return;
      }

      if (status === 403 && code === 'MUST_CHANGE_PASSWORD') {
        history.replace(CHANGE_PASSWORD_PATH);
        return;
      }

      const requestUrl = String(error?.response?.config?.url || error?.config?.url || '');
      if (status === 403 && requestUrl.includes('/api/auth/guest-session')) {
        return;
      }

      // Blocking SaaS-style center prompt for permission / server failures.
      // Soft validation stays as page-level message.warning / toasts.
      if (status === 403 || (typeof status === 'number' && status >= 500)) {
        const messageText =
          typeof detail === 'string'
            ? detail
            : typeof detail === 'object' && detail !== null && 'message' in detail
              ? String((detail as { message?: unknown }).message || 'Request failed')
              : status === 403
                ? 'You do not have permission to perform this action.'
                : 'The server returned an unexpected error. Please try again.';
        // Lazy import avoids circular init with layout
        void import('@/components/PortalErrorPrompt').then(({ showPortalError }) => {
          showPortalError({
            status,
            title: status === 403 ? 'Access denied' : 'Server error',
            message: messageText,
          });
        });
      }
    },
  },
};
